import { Component, inject, OnInit, AfterViewInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { Auth, EmailAuthProvider, GoogleAuthProvider } from '@angular/fire/auth';
import * as firebaseui from 'firebaseui';
import { fromEvent, take } from 'rxjs';

/**
 * LoginDialogComponent - Firebase UI in a Material Dialog
 * 
 * Simple dialog that shows Firebase UI for authentication.
 * Supports email/password and Google sign-in.
 * Event-based architecture, no setTimeout/setInterval.
 */
@Component({
  selector: 'app-login-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './login-dialog.component.html',
  styleUrl: './login-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  private dialogRef = inject(MatDialogRef<LoginDialogComponent>);
  private router = inject(Router);
  private auth = inject(Auth); // Inject modular Auth
  private ui: firebaseui.auth.AuthUI | null = null;

  ngOnInit() {
    // No initialization needed for modular SDK
  }

  ngAfterViewInit() {
    // Initialize Firebase UI after view is ready
    // We can initialize immediately if the container is ready, 
    // but for safety we can check if window is loaded if scripts were lazy loaded.
    // Since we are using imported modules, they should be available.
    
    if (document.readyState === 'complete') {
      this.initializeFirebaseUI();
    } else {
      fromEvent(window, 'load').pipe(take(1)).subscribe({
        next: () => this.initializeFirebaseUI(),
        error: () => this.initializeFirebaseUI()
      });
    }
  }

  ngOnDestroy() {
    // Clean up Firebase UI
    if (this.ui) {
      this.ui.delete();
    }
  }

  /**
   * Initialize Firebase UI with authentication providers
   */
  private initializeFirebaseUI() {
    if (typeof window === 'undefined') return;

    try {
      // Get or create Firebase UI instance
      // Note: AuthUI constructor takes the modular Auth instance
      this.ui = firebaseui.auth.AuthUI.getInstance() || 
                new firebaseui.auth.AuthUI(this.auth);

      // Firebase UI configuration
      const uiConfig: firebaseui.auth.Config = {
        callbacks: {
          signInSuccessWithAuthResult: (authResult: any) => {
            // User signed in successfully
            console.log('✅ [LoginDialog] Login successful:', authResult.user.email);
            
            // Close dialog
            this.dialogRef.close({ success: true, user: authResult.user });
            
            // Return false to prevent FirebaseUI redirect
            return false;
          },
          signInFailure: async (error: any) => {
            console.error('❌ [LoginDialog] Login failed:', error);
            return;
          },
          uiShown: () => {
            console.log('🎨 [LoginDialog] Firebase UI rendered');
          }
        },
        
        // Authentication providers
        signInOptions: [
          // Email/Password
          {
            provider: EmailAuthProvider.PROVIDER_ID,
            requireDisplayName: false,
            signInMethod: EmailAuthProvider.EMAIL_PASSWORD_SIGN_IN_METHOD
          },
          // Google Sign-In
          {
            provider: GoogleAuthProvider.PROVIDER_ID,
            customParameters: {
              prompt: 'select_account'
            }
          }
        ],
        
        tosUrl: '#',
        privacyPolicyUrl: '#',
        signInFlow: 'popup',
        credentialHelper: firebaseui.auth.CredentialHelper.GOOGLE_YOLO
      };

      // Start Firebase UI
      this.ui.start('#firebaseui-auth-container', uiConfig);
    } catch (error) {
      console.error('❌ [LoginDialog] Failed to initialize FirebaseUI:', error);
    }
  }

  /**
   * Close dialog
   */
  close() {
    this.dialogRef.close();
  }
}
